import React from 'react'
import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'


/** Minimal option shape: stable `id` and string `value` stored when selected. */
export type StringKeyedOption = {
	id: number | string
	value: string
}

export type OptionListAutocompleteProps<T extends StringKeyedOption> = {
	options: T[]
	/** Current stored value; should match `options[].value` when an option is selected. */
	value: string
	onChange: (nextValue: string) => void
	getOptionLabel: (option: T) => string
	isOptionEqualToValue?: (a: T, b: T) => boolean
	required?: boolean
	disabled?: boolean
	label: string
	helperText?: string
	placeholder?: string
	size?: 'small' | 'medium'
	fullWidth?: boolean
	noOptionsText?: string
	autoHighlight?: boolean
	id?: string
}

function defaultOptionEqual<T extends StringKeyedOption>(a: T, b: T): boolean {
	return a.id === b.id
}

/**
 * MUI Autocomplete restricted to predefined options (`freeSolo` off): type to filter, select to commit.
 * Use for dropdowns that need search without allowing arbitrary values.
 */
export function OptionListAutocomplete<T extends StringKeyedOption>(
	props: OptionListAutocompleteProps<T>,
) {
	const {
		options,
		value,
		onChange,
		getOptionLabel,
		isOptionEqualToValue = defaultOptionEqual,
		required = false,
		disabled = false,
		label,
		helperText,
		placeholder,
		size = 'small',
		fullWidth = true,
		noOptionsText = 'No matches',
		autoHighlight = true,
		id,
	} = props
	const selected = options.find((o) => o.value === value) ?? options[0]

	const renderInput = (params: AutocompleteRenderInputParams) => (
		<TextField
			{...params}
			label={label}
			size={size}
			fullWidth={fullWidth}
			required={required}
			helperText={helperText}
			placeholder={placeholder}
			InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
		/>
	)

	const shared = {
		options,
		value: selected,
		onChange: (_e: unknown, newValue: T | null) => {
			onChange(newValue?.value ?? '')
		},
		getOptionLabel,
		isOptionEqualToValue,
		disabled,
		autoHighlight,
		noOptionsText,
		renderInput,
	}

	return required ? (
		<Autocomplete<T, false, true, false> id={id} {...shared} disableClearable />
	) : (
		<Autocomplete<T, false, false, false> id={id} {...shared} />
	)
}
